import { useMemo, useState } from 'react';
import { Modal, Input, Checkbox, notification } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQuery } from '@apollo/client';
import debounce from 'lodash.debounce';
import { getSitesQuery } from '@src/util/query/site.query';
import { updateTaskInLibrary } from '@src/util/mutation/taskInLibrary.mutation';

export const AssociateSitesModal = ({visible, onClose, task, taskListRefetch}) => {

    const [siteIds, setSiteIds] = useState(task?.siteIds || []);
    const [selectedSites, setSelectedSites] = useState(
        task?.siteDetails?.map((site)=>({_id: site?._id, sitename: site?.sitename})) || []
    );
    const [search, setSearch] = useState('');

    const {data: siteData, loading: siteLoading} = useQuery(getSitesQuery, {
        fetchPolicy: 'cache-and-network',
        skip: !search,
        variables: {
            input: {
                filters: null,
                pageSize: 20,
                page: 1,
                search
            }
        }
    });
    const debouncedSearch = useMemo(()=>debounce((value)=>setSearch(value), 400), []);

    const visibleSites = useMemo(()=>{
        const searched = siteData?.sites?.sites || [];
        const merged = [...selectedSites];
        searched.forEach((site)=>{
            if(!merged.find((s)=>s._id===site._id)){
                merged.push(site);
            }
        });
        return merged;
    },[siteData, selectedSites]);

    const [updateTask, {loading: updateLoading}] = useMutation(updateTaskInLibrary);
    const [api, contextHolder] = notification.useNotification();

    const handleSave = async () => {
        await updateTask({
            variables: {
                input: {
                    _id: task?._id,
                    siteIds
                }
            }
        });
        api.success({
            message: 'Task was associated with sites',
            placement: 'top',
            className: 'notification-without-close',
        });
        await taskListRefetch();
        onClose();
    };

    return (
        <Modal
            title={`Associate "${task?.label}" with sites`}
            open={visible}
            onCancel={onClose}
            onOk={handleSave}
            okText="Save"
            okButtonProps={{disabled: siteIds?.length===0 || updateLoading, loading: updateLoading}}
        >
            {contextHolder}
            <Input
                type="search"
                className="generic-input-control"
                placeholder="Search sites..."
                style={{marginBottom: '12px'}}
                onChange={(e)=>debouncedSearch(e.target.value)}
                suffix={<FontAwesomeIcon style={{color:'#0091ae'}} icon={faSearch}/>}
            />
            <div style={{overflowY: 'scroll', maxHeight: '250px'}}>
                {!siteLoading && visibleSites?.map((site)=>(
                    <div className="popoverdataitem" key={site?._id}>
                        <Checkbox
                            onChange={(e)=>{
                                if(e.target.checked){
                                    setSiteIds([...siteIds, site?._id]);
                                    setSelectedSites([...selectedSites, site]);
                                }else{
                                    setSiteIds(siteIds?.filter((id)=>id!==site?._id));
                                    setSelectedSites(selectedSites?.filter((s)=>s._id!==site?._id));
                                }
                            }}
                            checked={!!siteIds?.find((id)=>id===site?._id)}
                        >
                            {site?.sitename}
                        </Checkbox>
                    </div>
                ))}
                {!search && visibleSites?.length===0 &&
                    <div style={{color: '#999'}}>Type to search sites</div>
                }
            </div>
        </Modal>
    );
};
